import React from 'react';
import Highlight from 'react-highlighter';
import { defineMessages, injectIntl } from 'react-intl';
import { Link } from 'react-router';
import moment from 'moment';

import { DATE_FORMAT } from '../../../config';
import Capitals from './Capitals';


const messages = defineMessages({
  CONVERSATION_FINISHED: {
    id: 'CustomerList.CONVERSATION_FINISHED',
    defaultMessage: 'Gespräch beendet',
  },
  CONVERSATION_STARTED: {
    id: 'CustomerList.CONVERSATION_STARTED',
    defaultMessage: 'Gespräch angefangen',
  },
  TOOK_SCREENSHOT: {
    id: 'CustomerList.TOOK_SCREENSHOT',
    defaultMessage: 'Screenshot aufgenommen',
  },
  APPLY_CUSTOMER: {
    id: 'CustomerList.APPLY_CUSTOMER',
    defaultMessage: 'Bisher wurden keine Kunden angelegt. Bitte verwenden Sie "Kunde anlegen" rechts oben um einen Kunden anzulegen.',
  },
  NOT_FOUND_CUSTOMER: {
    id: 'CustomerList.NOT_FOUND_CUSTOMER',
    defaultMessage: 'Es konnte kein Kunde gefunden werden.',
  },
  CLOCK: {
    id: 'CustomerList.CLOCK',
    defaultMessage: 'Uhr',
  },
  NO_EVENTS: {
    id: 'CustomerList.NO_EVENTS',
    defaultMessage: 'Zu diesem Kunden gibt es noch keine Ereignisse.',
  },
  COLUMN_CUSTOMER: { id: 'CustomerList.COLUMN_CUSTOMER', defaultMessage: 'Kunde' },
  COLUMN_LAST_EVENT: { id: 'CustomerList.COLUMN_LAST_EVENT', defaultMessage: 'Letztes Ereignis' },
  COLUMN_ACTIONS: { id: 'CustomerList.COLUMN_ACTIONS', defaultMessage: 'Aktionen' },
  TOOLTIP_EMAIL: { id: 'CustomerList.TOOLTIP_EMAIL', defaultMessage: 'Einladung per E-Mail' },
  TOOLTIP_COPY_LINK: { id: 'CustomerList.TOOLTIP_COPY_LINK', defaultMessage: 'Link kopieren' },
  TOOLTIP_EDIT: { id: 'CustomerList.TOOLTIP_EDIT', defaultMessage: 'Kunde bearbeiten' },
  TOOLTIP_DELETE: { id: 'CustomerList.TOOLTIP_DELETE', defaultMessage: 'Kunde löschen' },
  TOOLTIP_START_CALL: { id: 'CustomerList.TOOLTIP_START_CALL', defaultMessage: 'Gespräch starten / Raum öffnen' },
  EMAIL_INVITE_BODY: { id: 'CustomerList.EMAIL_INVITE_BODY', defaultMessage: 'Sehr geehrte Damen und Herren,\n\nhiermit lade ich Sie, wie besprochen, zum Video Call ein.\n\nBitte klicken Sie auf den nachfolgenden Link, um dem virtuellen Raum beizutreten:\n[LINK]\n\nMit freundlichen Grüßen' },
});

function renderLastEvent(event, formatMessage, filter) {
  if (event.text) {
    return filter ? <Highlight search={filter}>{event.text}</Highlight> : event.text;
  }
  if (event.call_event_type === 'end') {
    return formatMessage(messages.CONVERSATION_FINISHED);
  }
  if (event.call_event_type === 'begin') {
    return formatMessage(messages.CONVERSATION_STARTED);
  }
  if (event.pictures && event.pictures.length > 0) {
    return formatMessage(messages.TOOK_SCREENSHOT);
  }
  return null;
}

class CustomerList extends React.Component {
  constructor(props) {
    super(props);
    const savedState = localStorage.getItem('CustomerListTableState');
    const defaultState = {
      sortColumn: 'last_event',
      sortDirection: 'desc',
      columnWidths: {
        col0: 300, // Kunde
        col1: 250, // Letztes Ereignis
      }
    };
    
    this.state = savedState ? { ...defaultState, ...JSON.parse(savedState), copiedId: null } : { ...defaultState, copiedId: null };
    this.resizingColumn = null;
    this.startX = 0;
    this.startWidth = 0;
  }

  componentWillUnmount() {
    document.removeEventListener('mousemove', this.handleResizeMove);
    document.removeEventListener('mouseup', this.handleResizeEnd);
  }

  saveState = () => {
    const { sortColumn, sortDirection, columnWidths } = this.state;
    localStorage.setItem('CustomerListTableState', JSON.stringify({ sortColumn, sortDirection, columnWidths }));
  }

  toggleSort = (column) => {
    this.setState(prevState => {
      let nextDir = 'asc';
      if (prevState.sortColumn === column) {
        nextDir = prevState.sortDirection === 'asc' ? 'desc' : 'asc';
      }
      return {
        sortColumn: column,
        sortDirection: nextDir
      };
    }, this.saveState);
  }

  handleResizeStart = (e, columnKey) => {
    e.preventDefault();
    e.stopPropagation();
    this.resizingColumn = columnKey;
    this.startX = e.pageX;
    this.startWidth = this.state.columnWidths[columnKey];
    
    document.addEventListener('mousemove', this.handleResizeMove);
    document.addEventListener('mouseup', this.handleResizeEnd);
  };

  handleResizeMove = (e) => {
    if (!this.resizingColumn) return;
    const diff = e.pageX - this.startX;
    const newWidth = Math.max(50, this.startWidth + diff);
    this.setState(prevState => ({
      columnWidths: {
        ...prevState.columnWidths,
        [this.resizingColumn]: newWidth
      }
    }));
  };

  handleResizeEnd = () => {
    this.resizingColumn = null;
    document.removeEventListener('mousemove', this.handleResizeMove);
    document.removeEventListener('mouseup', this.handleResizeEnd);
    this.saveState();
  };

  copyToClipboard = (e, text, id) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(text).then(() => {
      this.setState({ copiedId: id });
      setTimeout(() => {
        if (this.state.copiedId === id) {
          this.setState({ copiedId: null });
        }
      }, 2000);
    });
  }

  renderSortIcon = (column) => {
    const { sortColumn, sortDirection } = this.state;
    if (sortColumn !== column) {
      return <i className="fa fa-sort" style={{ marginLeft: '8px', color: 'var(--text-muted)', opacity: 0.3 }} />;
    }
    return <i className={`fa ${sortDirection === 'asc' ? 'fa-sort-alpha-asc' : 'fa-sort-alpha-desc'}`} style={{ marginLeft: '8px', color: 'var(--text-color)' }} />;
  }

  render() {
    const { items, filter, allItems, company, intl: { formatMessage } } = this.props;
    const { copiedId, sortColumn, sortDirection, columnWidths } = this.state;

    if (!(items && items.length)) {
      let message = formatMessage(messages.APPLY_CUSTOMER);
      if (allItems && allItems.length > 0) {
        message = formatMessage(messages.NOT_FOUND_CUSTOMER);
      }
      return <div style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>{message}</div>;
    }

    const sortedItems = [...items].sort((l, r) => {
      if (!sortColumn) return 0;
      
      let valL, valR;
      if (sortColumn === 'name') {
        valL = (l.name || '').toLowerCase();
        valR = (r.name || '').toLowerCase();
      } else if (sortColumn === 'last_event') {
        valL = l.last_event ? new Date(l.last_event.created).getTime() : 0;
        valR = r.last_event ? new Date(r.last_event.created).getTime() : 0;
      }

      if (valL === valR) return 0;
      if (valL < valR) return sortDirection === 'asc' ? -1 : 1;
      return sortDirection === 'asc' ? 1 : -1;
    });

    let customPages = '';
    if (company && company.hasCustomPages) {
      customPages = `/custom/${company.id}`;
    }

    const thBaseStyle = {
      padding: '16px 24px',
      color: 'var(--text-muted)',
      fontWeight: 'bold',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      position: 'relative',
      cursor: 'pointer',
      userSelect: 'none'
    };

    const getThStyle = (key) => ({
      ...thBaseStyle,
      width: `${columnWidths[key]}px`,
      minWidth: `${columnWidths[key]}px`,
      maxWidth: `${columnWidths[key]}px`,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    });

    const resizerStyle = {
      position: 'absolute',
      right: 0,
      top: 0,
      bottom: 0,
      width: '6px',
      cursor: 'col-resize',
      backgroundColor: 'transparent',
      zIndex: 1,
    };

    const renderResizer = (key) => (
      <div 
        className="column-resizer"
        style={resizerStyle} 
        onMouseDown={(e) => this.handleResizeStart(e, key)}
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--text-muted)'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
      />
    );

    return (
      <div style={{ width: '100%', overflowX: 'auto' }}>
        <table className="responsive-table" style={{ minWidth: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
          <thead>
            <tr style={{ backgroundColor: 'var(--panel-bg)', borderBottom: '1px solid var(--panel-border)' }}>
              <th onClick={() => this.toggleSort('name')} style={getThStyle('col0')}>
                {formatMessage(messages.COLUMN_CUSTOMER)}
                {this.renderSortIcon('name')}
                {renderResizer('col0')}
              </th>
              <th onClick={() => this.toggleSort('last_event')} style={getThStyle('col1')}>
                {formatMessage(messages.COLUMN_LAST_EVENT)}
                {this.renderSortIcon('last_event')}
                {renderResizer('col1')}
              </th>
              <th style={{ ...thBaseStyle, padding: '16px 24px', width: 'auto', textAlign: 'right', cursor: 'default' }}>
                {formatMessage(messages.COLUMN_ACTIONS)}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedItems.map(customer => {
              const callUrl = `${window.location.origin}${customPages}/v/${customer.slug}/`;
              const custLocale = customer.locale || 'de';
              const defaultEmailTexts = {
                de: 'Sehr geehrte Damen und Herren,\n\nhiermit lade ich Sie, wie besprochen, zum Video Call ein.\n\nBitte klicken Sie auf den nachfolgenden Link, um dem virtuellen Raum beizutreten:\n[LINK]\n\nMit freundlichen Grüßen',
                en: 'Dear Sir or Madam,\n\nI hereby invite you to the video call as discussed.\n\nPlease click on the following link to join the virtual room:\n[LINK]\n\nBest regards',
                fr: 'Madame, Monsieur,\n\nComme convenu, je vous invite à participer à l\'appel vidéo.\n\nVeuillez cliquer sur le lien ci-dessous pour rejoindre la salle virtuelle :\n[LINK]\n\nCordialement',
                es: 'Estimado cliente,\n\nPor la presente le invito a la videollamada según lo acordado.\n\nPor favor, haga clic en el siguiente enlace para unirse a la sala virtual:\n[LINK]\n\nSaludos cordiales'
              };
              const defaultEmailSubjects = {
                de: 'Einladung zum Video Call',
                en: 'Invitation to video call',
                fr: 'Invitation à l\'appel vidéo',
                es: 'Invitación a la videollamada'
              };
              const defaultEmail = defaultEmailTexts[custLocale] || defaultEmailTexts['de'];
              const emailTemplate = (company && company.templates && company.templates.email && company.templates.email[custLocale] && company.templates.email[custLocale].text)
                || (company && company.email_template)
                || defaultEmail;
              let emailBody = emailTemplate.replace(/\[LINK\]|\{link\}/gi, callUrl);
              if (!/\[LINK\]|\{link\}/i.test(emailTemplate)) {
                emailBody = emailBody.trim() + '\n\n' + callUrl;
              }
              const emailSubject = defaultEmailSubjects[custLocale] || defaultEmailSubjects['de'];
              
              const displayName = customer.name && customer.name.length > 20 ? customer.name.substring(0, 20) + '...' : customer.name;

              return (
                <tr key={customer.id} style={{ borderBottom: '1px solid var(--panel-border)', transition: 'background-color 0.2s', backgroundColor: customer.deleted ? 'rgba(239, 68, 68, 0.1)' : 'transparent', ':hover': { backgroundColor: 'var(--btn-default-hover-bg)' } }}>
                  <td style={{ padding: '16px 24px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Link to={`/company/${customer.company_id}/customer/${customer.id}/`} style={{ fontWeight: 'bold', color: 'var(--text-color)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '16px', overflow: 'hidden' }}>
                      <Capitals customer={customer} />
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <div style={{ fontSize: '15px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{filter ? <Highlight search={filter}>{displayName}</Highlight> : displayName}</div>
                        <div style={{ fontSize: '13px', color: 'var(--text-muted)', fontWeight: 'normal', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{filter ? <Highlight search={filter}>{customer.deleted || customer.phone}</Highlight> : (customer.deleted || customer.phone)}</div>
                      </div>
                    </Link>
                  </td>
                  <td className="last-event-cell" data-label={formatMessage(messages.COLUMN_LAST_EVENT)} style={{ padding: '16px 24px', color: 'var(--text-color)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {customer.last_event ? (
                      <div className="last-event-cell-wrapper">
                        <div className="last-event-date" style={{ fontWeight: 'bold', fontSize: '13px', color: 'var(--text-color)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {moment(customer.last_event.created, DATE_FORMAT).format('DD.MM.YY')} - {moment(customer.last_event.created, DATE_FORMAT).format('HH:mm')} {formatMessage(messages.CLOCK)}
                        </div>
                        <div className="last-event-text" style={{ fontSize: '13px', marginTop: '2px', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {renderLastEvent(customer.last_event, formatMessage, filter)}
                        </div>
                      </div>
                    ) : (
                      <div className="last-event-cell-wrapper">
                        <div className="last-event-text" style={{ color: 'var(--text-muted)', fontSize: '13px', fontStyle: 'italic', marginTop: '4px' }}>
                          {formatMessage(messages.NO_EVENTS)}
                        </div>
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '16px 24px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px' }}>
                      
                      <a 
                        href={`mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`}
                        title={formatMessage(messages.TOOLTIP_EMAIL)}
                        style={{ 
                          width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          borderRadius: '6px', 
                          backgroundColor: 'var(--btn-default-bg)', 
                          color: 'var(--text-muted)', 
                          border: '1px solid var(--btn-default-border)', 
                          textDecoration: 'none', transition: 'all 0.2s',
                        }}
                      >
                        <i className="fa fa-envelope" />
                      </a>

                      <button
                        onClick={(e) => this.copyToClipboard(e, callUrl, customer.id)}
                        title={formatMessage(messages.TOOLTIP_COPY_LINK)}
                        style={{ 
                          width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          borderRadius: '6px', 
                          backgroundColor: copiedId === customer.id ? 'rgba(34, 197, 94, 0.15)' : 'var(--btn-default-bg)', 
                          color: copiedId === customer.id ? '#22c55e' : 'var(--text-muted)', 
                          border: `1px solid ${copiedId === customer.id ? '#22c55e' : 'var(--btn-default-border)'}`, 
                          cursor: 'pointer', outline: 'none', transition: 'all 0.2s',
                        }}
                      >
                        <i className={`fa ${copiedId === customer.id ? 'fa-check' : 'fa-clipboard'}`} />
                      </button>

                      <Link
                        to={`/company/${customer.company_id}/customer/${customer.id}/update`}
                        title={formatMessage(messages.TOOLTIP_EDIT)}
                        style={{ 
                          width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          borderRadius: '6px', 
                          backgroundColor: 'var(--btn-default-bg)', 
                          color: 'var(--text-muted)', 
                          border: '1px solid var(--btn-default-border)', 
                          cursor: 'pointer', outline: 'none', transition: 'all 0.2s',
                        }}
                      >
                        <i className="fa fa-pencil" />
                      </Link>

                      <Link 
                        to={`/company/${customer.company_id}/customer/${customer.id}/`}
                        title={formatMessage(messages.TOOLTIP_START_CALL)}
                        style={{ 
                          width: '36px', height: '36px', borderRadius: '50%', 
                          display: 'flex', alignItems: 'center', justifyContent: 'center', 
                          color: '#fff', backgroundColor: '#ef4444', 
                          boxShadow: '0 2px 4px rgba(239,68,68,0.3)',
                          textDecoration: 'none', transition: 'transform 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                      >
                        <i className="icon-phone" style={{ fontSize: '16px' }} />
                      </Link>

                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }
}

export default injectIntl(CustomerList);
